import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import DialogModal from 'src/components/ui/dialogModal';
import { formatMoney } from 'src/utils/formatMoney';
import { IUser } from 'src/utils/interfaces/user';
import { PaymentDataResponse } from 'src/services/models/payment.service';
import { PaymentsService } from 'src/services/payments.service';
import { Permissions } from 'src/utils/enums/permissions.enum';
import Spinner from 'src/components/ui/spinner';
import { Tabs } from '../index';
import TooltipIcon from 'src/components/ui/tooltip';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import {
    AccountType,
    BankAccountType,
    Country,
    PurchaseType,
    SecurityDepositWaiverType,
} from 'src/utils/enums/payment.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FULL_NAME, USER_NAME } from '../../../../../config';
import {
    getBrandByDealerNumber,
    getPermissionsMap,
    isAllow,
    isSiteCanadian,
} from 'src/utils/helpers';
import { useDispatch, useSelector } from 'react-redux';

const ZERO = 0;

interface IProps {
    type: Tabs;
    paymentData: PaymentDataResponse;
    setPaymentInfo: Dispatch<
        SetStateAction<{
            show: boolean;
            type: Tabs;
            paymentData: PaymentDataResponse;
        }>
    >;
    notify: boolean;
    setNotify: Dispatch<boolean>;
    isError: boolean;
    setIsError: Dispatch<boolean>;
    setWarning: Dispatch<boolean>;
    isSavings: Function;
}

interface IInputProps extends IItemLabelProps, IErrorLabelProps {
    value: string | number;
    isError: boolean;
    isReadOnly: boolean;
    onChangeHandler: Function;
    maxLength?: number;
    isDisabled: boolean;
    showDollar?: boolean;
}

interface IComboProps extends IItemLabelProps, IErrorLabelProps {
    options: IOption[];
    selectedOption: string;
    isError: boolean;
    onSelectHandler: Function;
    placeHolder?: string;
    name: string;
    isDisabled: boolean;
}

interface IItemLabelProps {
    label: string;
    tooltip?: string;
}

interface IErrorLabelProps {
    errorLabel: string;
}

interface IOption {
    value: string;
    label: string;
}

const LayoutItem: React.FC = ({ children }) => {
    return (
        <div className='o-layout__item  u-1/2  u-1/1@s'>
            <div className='c-form-field'>
                <div className='c-form-field__box'>{children}</div>
            </div>
        </div>
    );
};

const ItemLabel: React.FC<IItemLabelProps> = ({ label, tooltip }) => {
    return (
        <div>
            <label
                htmlFor='m1l7e063'
                className='c-form-field__label u-mr-xsmall'
            >
                {label}
            </label>
            {tooltip && <TooltipIcon text={tooltip}></TooltipIcon>}
        </div>
    );
};

const ErrorLabel: React.FC<IErrorLabelProps> = ({ errorLabel }) => {
    return <span className={'c-error-message'}>{errorLabel}</span>;
};

const PaymentInput: React.FC<IInputProps> = ({
    label,
    value,
    errorLabel,
    isReadOnly,
    isError,
    onChangeHandler,
    tooltip = '',
    maxLength = 10,
    isDisabled,
    showDollar = true,
}) => {
    return (
        <LayoutItem>
            <ItemLabel label={label} tooltip={tooltip} />
            <div className='c-input align'>
                {showDollar && <span>$</span>}
                <input
                    className={`c-input__input ${isError ? 'is-error' : ''} ${
                        isReadOnly ? 'bold' : ''
                    }`}
                    onChange={(e) => onChangeHandler(e)}
                    type='text'
                    value={value}
                    maxLength={maxLength}
                    readOnly={isDisabled}
                />
            </div>
            <div>{isError ? <ErrorLabel errorLabel={errorLabel} /> : null}</div>
        </LayoutItem>
    );
};

const PaymentCombo: React.FC<IComboProps> = ({
    label,
    selectedOption,
    options,
    errorLabel,
    isError,
    onSelectHandler,
    name,
    tooltip = '',
    placeHolder = '',
    isDisabled = false,
}) => {
    return (
        <LayoutItem>
            <ItemLabel label={label} tooltip={tooltip} />
            <div className='c-input'>
                <ComboBox
                    name={name}
                    options={options}
                    defaultValue={''}
                    placeholder={placeHolder}
                    onSelect={(v) => onSelectHandler(v)}
                    selectedOption={selectedOption}
                    disabled={isDisabled}
                />
            </div>
            <div>{isError ? <ErrorLabel errorLabel={errorLabel} /> : null}</div>
        </LayoutItem>
    );
};

const PaymentInformation: React.FC<IProps> = ({
    paymentData,
    type,
    notify,
    setNotify,
    setIsError,
    isError,
    setPaymentInfo,
    setWarning,
    isSavings,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const odoCheck =
        /^(?: [1-9][0-9]{6}|[1-9][0-9]{5}|[1-9][0-9]{4}|[1-9][0-9]{3}|[1-9][0-9]{2}|[1-9][0-9]|[1-9])$/;
    const defaultDecimals = 2;
    const [loading, setLoading] = useState(false);
    const [country, setCountry] = useState<string>('Canada');
    const [dealerName, setDealerName] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState(false);
    const { payments, payoffPaymentInformation } = useLocalization();
    const [showConfimationDialog, setShowConfimationDialog] = useState(false);
    const units = isSiteCanadian() ? '(km)' : '(miles)';

    const editPaymentInfo = useSelector((state: IUser) => {
        return state.paymentInfo;
    });

    const [brand, setBrand] = useState<string>('vw6');

    const { quoteDetails, bankAccountDetails, accountDetail } = paymentData;

    const taxAmount =
        (editPaymentInfo || {}).TaxAmount ||
        (quoteDetails && isOperatingLease()
            ? +quoteDetails.residualTax +
              +quoteDetails.taxOnFees +
              +quoteDetails.pastDueTaxAmt +
              +quoteDetails.futurePaymentTaxAmt
            : ZERO);
    const amountBeforeTax =
        (editPaymentInfo || {}).AmountBeforeTax ||
        (quoteDetails && isOperatingLease()
            ? +quoteDetails.quoteAmount - taxAmount
            : ZERO);
    const defaultSecurityDeposit = '0.00';
    const defaultLpda = '0';
    const securityDepositAmount = isOperatingLease()
        ? quoteDetails?.securityDeposit
        : defaultSecurityDeposit;

    const [dealerID, setDealerID] = useState('');
    const [person, setPerson] = useState({ name: ' ', email: '' });
    const [payment, setPayment] = useState({
        amount: (editPaymentInfo || {}).PaymentAmount
            ? editPaymentInfo.PaymentAmount
            : isOperatingLease()
            ? formatMoney(amountBeforeTax + taxAmount, false)
            : paymentData
            ? paymentData.quoteDetails?.quoteAmount
            : '0.00',
        dealer: (editPaymentInfo || {}).DealerReferenceId || '',
        odometerReading: (editPaymentInfo || {}).Mileage || '',
        securityDeposit:
            (editPaymentInfo || {}).SecurityDepositAmount ||
            securityDepositAmount,
        lpaAdjustment:
            (editPaymentInfo || {}).LeasePullAheadAmount || defaultLpda,
        loyaltyVin: (editPaymentInfo || {}).LeasePullAheadVIN || '',
        purchaseType:
            (editPaymentInfo || {}).PurchaseType ||
            PurchaseType.DealerInventory,
        paymentType:
            ((editPaymentInfo || {}).BankAccount || {}).BankAccountType ||
            isSavings(bankAccountDetails.accountType)
                ? BankAccountType.Saving
                : BankAccountType.Checking,
        assignSecurityDeposit:
            (editPaymentInfo || {}).SecurityDepositAssignmentType ||
            SecurityDepositWaiverType.AssignToCustomer,
        total:
            amountBeforeTax +
            taxAmount +
            +defaultSecurityDeposit +
            +defaultLpda,
        errAmount: false,
        errDealer: false,
        errOdometerReading: false,
        errLpaAdjustment: false,
        errLoyaltyVin: false,
        errPurchaseType: false,
    });

    const permissionsMap: Map<string, Array<string>> = getPermissionsMap();
    const currentDealers = useSelector((state: IUser) => {
        return state.currentDealers;
    });
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const permissionsToken = useSelector((state: IUser) => {
        return state.permissionsToken;
    });

    const [payPayOffErrorAmount, setPayPayOffErrorAmount] = useState(false);

    useEffect(() => {
        if (permissionsToken) {
            const fullname = sessionStorage.getItem(FULL_NAME);
            const username = sessionStorage.getItem(USER_NAME);
            if (fullname && username) {
                setPerson({
                    name: fullname,
                    email: username,
                });
            }
        }
    }, []);
    useEffect(() => {
        if (currentDealers && dealerDataInfo) {
            const { dealer } = dealerDataInfo;
            setBrand(getBrandByDealerNumber(dealer.brand, dealer.dealerNumber));
            setDealerID(dealer.dealerNumber);
            setCountry(dealer.country);
            setDealerName(dealer.dealerName);
        }
    }, [dealerDataInfo]);

    function getSubTotalAndWarning() {
        const quoteSecurityDeposit =
            paymentData?.quoteDetails?.securityDeposit ??
            defaultSecurityDeposit;
        const warningFlag =
            payment.assignSecurityDeposit == SecurityDepositWaiverType.Deduct ||
            payment.assignSecurityDeposit ==
                SecurityDepositWaiverType.AssignToDealer;

        let subTotal = amountBeforeTax + taxAmount + +defaultSecurityDeposit;
        if (payment.assignSecurityDeposit == SecurityDepositWaiverType.Deduct) {
            subTotal = amountBeforeTax + taxAmount - +quoteSecurityDeposit;
        }

        return [subTotal, warningFlag];
    }

    useEffect(() => {
        const [subTotal, warningFlag] = getSubTotalAndWarning();

        setWarning(warningFlag);
        setPayment((oldValue) => {
            return {
                ...oldValue,
                total: subTotal - +oldValue.lpaAdjustment,
            };
        });
    }, [payment.assignSecurityDeposit]);

    useEffect(() => {
        const [subTotal] = getSubTotalAndWarning();
        setPayment((oldValue) => {
            return {
                ...oldValue,
                total: subTotal - +oldValue.lpaAdjustment,
            };
        });
    }, [payment.lpaAdjustment]);

    useEffect(
        () => () => {
            dispatch({
                type: 'DeletePaymentInfo',
            });
        },
        [],
    );

    function isOperatingLeaseOrUndefined(value: any) {
        return isOperatingLease() ? value : undefined;
    }

    const isSubmitForProcessing = isAllow(
        [Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE],
        permissionsMap,
        dealerID,
    );

    const validateAmount = () => {
        let endValidations = false;
        const amountCheck = /^[0-9]+((\.|,)[0-9]{1,2})?$/;
        if (!isOperatingLease() && amountCheck.test(payment.amount) === false) {
            setPayment((oldValue) => {
                return { ...oldValue, errAmount: true };
            });
            setLoading(false);
            endValidations = true;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, errAmount: false };
            });
        }
        return endValidations;
    };

    const validateDealer = () => {
        let endValidations = false;
        if (payment.dealer.length) {
            const dealerCheck = /^[a-zA-Z0-9]*$/;
            if (dealerCheck.test(payment.dealer) === false) {
                setPayment((oldValue) => {
                    return { ...oldValue, errDealer: true };
                });
                setLoading(false);
                endValidations = true;
            } else {
                setPayment((oldValue) => {
                    return { ...oldValue, errDealer: false };
                });
            }
        }
        return endValidations;
    };

    const validateLoyaltyVin = () => {
        let endValidations = false;
        const loyaltyVinCheck = /^[A-HJ-NPR-Z0-9]{17}$/;
        if (
            payment.loyaltyVin &&
            loyaltyVinCheck.test(payment.loyaltyVin) === false
        ) {
            setPayment((oldValue) => {
                return { ...oldValue, errLoyaltyVin: true };
            });
            setLoading(false);
            endValidations = true;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, errLoyaltyVin: false };
            });
        }
        return endValidations;
    };

    const validateLpa = () => {
        let endValidations = false;
        const lpaCheck = /^(?:5000(?:\.00?)?|[0-4]?\d{0,3}(?:\.\d{1,2})?)$/;
        if (
            lpaCheck.test(payment.lpaAdjustment) === false ||
            (payment.lpaAdjustment == '' && payment.loyaltyVin)
        ) {
            setPayment((oldValue) => {
                return { ...oldValue, errLpaAdjustment: true };
            });
            setLoading(false);
            endValidations = true;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, errLpaAdjustment: false };
            });
        }
        return endValidations;
    };

    const validateOdometer = () => {
        let endValidations = false;
        if (odoCheck.test(payment.odometerReading) === false) {
            setPayment((oldValue) => {
                return { ...oldValue, errOdometerReading: true };
            });
            setLoading(false);
            endValidations = true;
        } else {
            setPayment((oldValue) => {
                return { ...oldValue, errOdometerReading: false };
            });
        }
        return endValidations;
    };

    const validateQuote = () => {
        let endValidations = false;
        if (payment.amount > paymentData.quoteDetails!.quoteAmount!) {
            setLoading(false);
            setPayPayOffErrorAmount(true);
            endValidations = true;
        } else {
            setPayPayOffErrorAmount(false);
        }
        return endValidations;
    };

    const onClickSubmit = () => {
        setLoading(true);

        if (validateAmount()) return;
        if (validateQuote()) return;

        if (validateDealer()) return;

        if (isOperatingLease()) {
            if (validateOdometer()) return;
            if (validateLoyaltyVin()) return;
            if (validateLpa()) return;

            if (
                parseFloat(payment.lpaAdjustment) > ZERO &&
                payment.purchaseType != PurchaseType.DealerInventory
            ) {
                setPayment((oldValue) => {
                    return { ...oldValue, errPurchaseType: true };
                });
                setLoading(false);
                return;
            } else {
                setPayment((oldValue) => {
                    return { ...oldValue, errPurchaseType: false };
                });
            }
        }
        isSubmitForProcessing ? setShowConfimationDialog(true) : handleSubmit();
    };

    const handleSubmit = async () => {
        if (showConfimationDialog) setShowConfimationDialog(false);
        const paymentSave = {
            SelectedPaymentReason: 'Other',
            AccountNumber: accountDetail.accountNumber,
            Vin: accountDetail.vin,
            AccountType: accountDetail.accountType,
            BankAccount: {
                AccountNumber: bankAccountDetails.accountNumber,
                RoutingNumber: bankAccountDetails.routingNumber,
                ccNumber: bankAccountDetails.ccNumber,
                BankAccountType: payment.paymentType,
            },
            DealerId: dealerID,
            DealerName: dealerName,
            DealerReferenceId: payment.dealer,
            DealerComment: 'Payoff Payment',
            Country: country,
            PaymentAmount: isOperatingLease()
                ? +payment.total.toFixed(defaultDecimals)
                : +payment.amount,
            TransactionType: type,
            PaymentProcessStatus: isAllow(
                [Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE],
                permissionsMap,
                dealerID,
            )
                ? Number('1')
                : Number('0'),
            UserID: person.email,
            DealerBrand: brand.toLocaleLowerCase().includes('vw')
                ? 'VOLKSWAGEN'
                : brand.toLocaleLowerCase().includes('audi')
                ? 'AUDI'
                : 'DUCATI',
            SecurityDepositAmount: isOperatingLeaseOrUndefined(
                +payment.securityDeposit,
            ),
            SecurityDepositAssignmentType: isOperatingLeaseOrUndefined(
                payment.assignSecurityDeposit,
            ),
            Mileage: isOperatingLeaseOrUndefined(+payment.odometerReading),
            PurchaseType: isOperatingLeaseOrUndefined(payment.purchaseType),
            LeasePullAheadVIN: isOperatingLeaseOrUndefined(payment.loyaltyVin),
            LeasePullAheadAmount: isOperatingLeaseOrUndefined(
                +payment.lpaAdjustment,
            ),
            CcNumber: isOperatingLeaseOrUndefined(bankAccountDetails.ccNumber),
            TaxAmount: isOperatingLeaseOrUndefined(taxAmount),
            AmountBeforeTax: isOperatingLeaseOrUndefined(amountBeforeTax),
            VehicleMake: isOperatingLeaseOrUndefined(accountDetail.vehicleMake),
            BodyStyle: isOperatingLeaseOrUndefined(accountDetail.bodyStyle),
            VehicleYear: isOperatingLeaseOrUndefined(accountDetail.vehicleYear),
        };

        const service = new PaymentsService();
        try {
            let res;
            if (
                payment.dealer == editPaymentInfo?.DealerReferenceId &&
                (Number(payment.total.toFixed(defaultDecimals)) ==
                    editPaymentInfo?.PaymentAmount ||
                    Number(payment.amount) == editPaymentInfo?.PaymentAmount) &&
                Number(payment.securityDeposit) ==
                    editPaymentInfo?.SecurityDepositAmount &&
                payment.assignSecurityDeposit ==
                    editPaymentInfo?.SecurityDepositAssignmentType &&
                Number(payment.odometerReading) == editPaymentInfo?.Mileage &&
                payment.purchaseType == editPaymentInfo?.PurchaseType &&
                payment.loyaltyVin == editPaymentInfo?.LeasePullAheadVIN &&
                Number(payment.lpaAdjustment) ==
                    editPaymentInfo?.LeasePullAheadAmount
            ) {
                setNotify(true);
                setIsError(false);
                setLoading(false);
                setIsSuccess(true);
                return;
            }
            if (editPaymentInfo) {
                res = await service.paymentEdit({
                    ...paymentSave,
                    PaymentTransactionId: editPaymentInfo.PaymentTransactionId,
                });
            } else {
                res = await service.paymentSave(paymentSave);
            }
            if (res.data?.Payment) {
                setNotify(true);
                setIsError(false);
            } else {
                setNotify(true);
                setIsError(true);
            }
            setLoading(false);
            setIsSuccess(true);
        } catch (error) {
            setNotify(true);
            setIsError(true);
            setLoading(false);
            setIsSuccess(false);
        }
    };

    function onCancel() {
        dispatch({
            type: 'DeletePaymentInfo',
        });
        if (editPaymentInfo) {
            navigate('/dashboard/payments/pending');
        }
        setPaymentInfo({
            show: false,
            type: Tabs.PAYOFF,
            paymentData: {
                accountDetail: {
                    accountNumber: '',
                    country: Country.Canada,
                    customerName: '',
                    vehicleMake: '',
                    vehicleModel: '',
                    vehicleYear: '',
                    bodyStyle: '',
                    maturityDate: '',
                    accountType: AccountType.Unknown,
                    accountStatus: '',
                    address: {
                        StateOrProvince: '',
                        TypeCode: '',
                    },
                    prePayLease: false,
                    singlePayLease: false,
                    repossessed: false,
                    vin: '',
                    processStatus: '',
                    TermTypeStatus: '',
                    balloonFinanceType: '',
                    paymentSource: '',
                    contractTerm: 0,
                    partyId: 0,
                },
                privacyFlag: false,
                isBrassHatDealer: false,
                bankAccountDetails: {
                    ccNumber: 0,
                    accountNumber: '',
                    routingNumber: '',
                    accountType: 0,
                },
            },
        });
    }

    function isOperatingLease() {
        return (
            paymentData.accountDetail.accountType == AccountType.OperatingLease
        );
    }

    const dealerRefIdOnChangeHandler = (event: any) => {
        const reg = /^[a-zA-Z0-9]*$/;
        if (reg.test(event.target.value)) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    dealer: event.target.value.toLocaleUpperCase(),
                };
            });
        }
    };

    const dealerRefId = (
        <PaymentInput
            label={payments.PAYMENT_INFORMATION_DEALERREFLABEL}
            value={payment.dealer}
            isReadOnly={false}
            isError={payment.errDealer}
            onChangeHandler={dealerRefIdOnChangeHandler}
            errorLabel={payments.PAYMENT_INFORMATION_DEALERREFERR}
            isDisabled={isSuccess}
            showDollar={false}
        />
    );

    const PaymentAmount = (
        <PaymentInput
            label={payments.PAYMENT_INFORMATION_AMOUNTLABEL}
            value={payment.amount}
            isReadOnly={isOperatingLease()}
            isError={payment.errAmount || payPayOffErrorAmount}
            onChangeHandler={() => {
                return;
            }}
            errorLabel={
                payment.errAmount
                    ? payments.PAYMENT_INFORMATION_AMOUNTERR
                    : payPayOffErrorAmount
                    ? payments.PAYMENT_INFORMATION_AMOUNTERRPAYOFF
                    : ''
            }
            isDisabled={isOperatingLease() ? false : isSuccess}
            showDollar={true}
        />
    );

    const odometerReadingOnChangeHandler = (event: any) => {
        if (event.target.value === '' || odoCheck.test(event.target.value)) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    odometerReading: event.target.value,
                };
            });
        }
    };

    const lpaTooltip =
        brand == 'audi'
            ? payoffPaymentInformation.LPA_AUDI_TOOLTIP
            : payoffPaymentInformation.LPA_VW_TOOLTIP;

    const lpaOnChangeHandler = (event: any) => {
        const re = /^[\d.]+$/;
        if (event.target.value === '' || re.test(event.target.value)) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    lpaAdjustment: event.target.value,
                };
            });
        }
    };

    const loyaltyVinOnChangeHandler = (event: any) => {
        const re = /^[a-zA-Z0-9]+$/;
        if (event.target.value === '' || re.test(event.target.value)) {
            setPayment((oldValue) => {
                return {
                    ...oldValue,
                    loyaltyVin: event.target.value,
                };
            });
        }
    };

    const purchaseTypeOptions = [
        {
            value: PurchaseType.DealerInventory,
            label: payoffPaymentInformation.PURCHASE_TYPE_DEALER,
        },
        {
            value: PurchaseType.CustomerBuyBack,
            label: payoffPaymentInformation.PURCHASE_TYPE_CUSTOMER,
        },
    ];
    const purchaseTypeOnSelectHandler = (value: any) => {
        setPayment((oldValue) => {
            return {
                ...oldValue,
                purchaseType: value,
            };
        });
    };

    const assignSecurityDepositOptions = [
        {
            value: SecurityDepositWaiverType.AssignToCustomer,
            label: payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_CUSTOMER,
        },
        {
            value: SecurityDepositWaiverType.AssignToDealer,
            label: payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_DEALER,
        },
        {
            value: SecurityDepositWaiverType.Deduct,
            label: payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_DEDUCT,
        },
    ];
    const assignSecurityDepositOnSelectHandler = (value: any) => {
        setPayment((oldValue) => {
            return {
                ...oldValue,
                assignSecurityDeposit: value,
            };
        });
    };

    function getPaymentTypeOptions() {
        const options = [];

        if (isSavings(bankAccountDetails.accountType)) {
            options.push({
                value: BankAccountType.Saving,
                label: payoffPaymentInformation.PAYMENT_TYPE_SAVINGS,
            });
        } else {
            options.push({
                value: BankAccountType.Checking,
                label: payoffPaymentInformation.PAYMENT_TYPE_CHEQUE,
            });

            if (paymentData.isBrassHatDealer) {
                options.push({
                    value: BankAccountType.CreditLine,
                    label: payoffPaymentInformation.PAYMENT_TYPE_BRASS,
                });
            }
        }
        return options;
    }

    const paymentTypeOptions = getPaymentTypeOptions();
    const paymentTypeOnSelectHandler = (value: any) => {
        setPayment((oldValue) => {
            return {
                ...oldValue,
                paymentType: value,
            };
        });
    };

    function isLPAEmptyOrValid() {
        return (
            (!payment.loyaltyVin &&
                (!payment.lpaAdjustment ||
                    payment.lpaAdjustment == defaultLpda)) ||
            (payment.loyaltyVin.length &&
                payment.lpaAdjustment != defaultLpda &&
                ('' + payment.lpaAdjustment).length)
        );
    }
    return (
        <>
            <DialogModal
                titleContent={
                    payments.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_TITLE
                }
                bodyContent=''
                open={showConfimationDialog}
                cancelBtnLabel={
                    payments.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_CANCEL
                }
                confirmBtnLabel={
                    payments.PAYMENT_INFORMATION_DETAILS_APPROVE_CONFIRM_PROCEED
                }
                onCancelHandler={() => {
                    setShowConfimationDialog(false);
                    setLoading(false);
                }}
                onConfirmHandler={() => {
                    handleSubmit();
                }}
                isContentCentered={false}
                showButtons={true}
                icon={''}
                isBigModal={false}
                isError={false}
                progress={undefined}
                modalExtraClass='payment-approval-confirmation-modal'
            />
            <section className='c-form-section payoff_information_section'>
                <div className='c-form-section__content'>
                    <div className='title'>
                        <h6>
                            <span className='c-section-heading__title-text'>
                                {editPaymentInfo
                                    ? payments.PAYMENT_EDIT_INFORMATION_TITLE
                                    : payments.PAYMENT_INFORMATION_TITLE}
                            </span>
                        </h6>
                    </div>
                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            {isOperatingLease() ? PaymentAmount : dealerRefId}
                            {isOperatingLease() ? (
                                <PaymentInput
                                    label={`${payoffPaymentInformation.ODOMETER_READING} ${units}`}
                                    value={payment.odometerReading}
                                    isReadOnly={false}
                                    isError={payment.errOdometerReading}
                                    onChangeHandler={
                                        odometerReadingOnChangeHandler
                                    }
                                    errorLabel={
                                        payoffPaymentInformation.ODOMETER_READING_ERROR
                                    }
                                    isDisabled={isSuccess}
                                    showDollar={false}
                                />
                            ) : null}
                        </div>
                    </div>
                    {isOperatingLease() && (
                        <>
                            <div className='o-fieldset__row u-mt-large u-mb-large'>
                                <div className='o-layout'>
                                    <PaymentInput
                                        label={
                                            payoffPaymentInformation.SECURITY_DEPOSIT
                                        }
                                        value={formatMoney(
                                            payment.securityDeposit,
                                        )}
                                        isReadOnly={true}
                                        isError={false}
                                        onChangeHandler={() => {
                                            return;
                                        }}
                                        errorLabel=''
                                        isDisabled={false}
                                        showDollar={false}
                                    />
                                    <PaymentCombo
                                        errorLabel={
                                            payoffPaymentInformation.PURCHASE_TYPE_ERROR
                                        }
                                        isError={payment.errPurchaseType}
                                        label={
                                            payoffPaymentInformation.PURCHASE_TYPE_LABEL
                                        }
                                        name='PurchaseTypeSelector'
                                        selectedOption={payment.purchaseType}
                                        options={purchaseTypeOptions}
                                        onSelectHandler={
                                            purchaseTypeOnSelectHandler
                                        }
                                        isDisabled={isSuccess}
                                    />
                                </div>
                            </div>
                            <div className='o-fieldset__row u-mt-large u-mb-large'>
                                <div className='o-layout'>
                                    <PaymentCombo
                                        errorLabel=''
                                        isError={false}
                                        label={
                                            payoffPaymentInformation.ASSIGN_SECURITY_DEPOSIT_LABEL
                                        }
                                        name='AssingSecurityDepositSelector'
                                        selectedOption={
                                            payment.assignSecurityDeposit
                                        }
                                        options={assignSecurityDepositOptions}
                                        onSelectHandler={
                                            assignSecurityDepositOnSelectHandler
                                        }
                                        isDisabled={isSuccess}
                                    />
                                    <PaymentCombo
                                        errorLabel=''
                                        isError={false}
                                        label={
                                            payoffPaymentInformation.PAYMENT_TYPE_LABEL
                                        }
                                        name='PaymentTypeSelector'
                                        selectedOption={payment.paymentType}
                                        options={paymentTypeOptions}
                                        onSelectHandler={
                                            paymentTypeOnSelectHandler
                                        }
                                        isDisabled={isSuccess}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <div className='o-fieldset__row u-mt-large u-mb-large'>
                        <div className='o-layout'>
                            {isOperatingLease() ? (
                                <PaymentInput
                                    label={payoffPaymentInformation.LPA_LABEL}
                                    value={payment.lpaAdjustment}
                                    isReadOnly={false}
                                    isError={payment.errLpaAdjustment}
                                    onChangeHandler={lpaOnChangeHandler}
                                    errorLabel={
                                        payoffPaymentInformation.LPA_ERROR
                                    }
                                    tooltip={lpaTooltip}
                                    isDisabled={isSuccess}
                                />
                            ) : (
                                PaymentAmount
                            )}
                            {isOperatingLease() ? dealerRefId : null}
                        </div>
                    </div>
                    {isOperatingLease() && (
                        <>
                            <div className='o-fieldset__row u-mt-large u-mb-large'>
                                <div className='o-layout'>
                                    <PaymentInput
                                        label={
                                            payoffPaymentInformation.LOYALTY_VIN
                                        }
                                        value={payment.loyaltyVin}
                                        isReadOnly={false}
                                        isError={payment.errLoyaltyVin}
                                        onChangeHandler={
                                            loyaltyVinOnChangeHandler
                                        }
                                        errorLabel={
                                            payoffPaymentInformation.LOYALTY_VIN_ERROR
                                        }
                                        maxLength={17}
                                        isDisabled={isSuccess}
                                        showDollar={false}
                                    />
                                    <LayoutItem>
                                        <div>
                                            <span className='bold'>
                                                {
                                                    payoffPaymentInformation.DISCLAIMER_TITLE
                                                }
                                            </span>
                                            <br />
                                            {
                                                payoffPaymentInformation.INFORMATION_DISCLAIMER
                                            }
                                        </div>
                                    </LayoutItem>
                                </div>
                            </div>
                            <div className='o-fieldset__row u-mt-large u-mb-large'>
                                <div className='o-layout'>
                                    <PaymentInput
                                        label={payoffPaymentInformation.TOTAL}
                                        value={formatMoney(payment.total)}
                                        isReadOnly={true}
                                        isError={false}
                                        onChangeHandler={() => {
                                            return;
                                        }}
                                        errorLabel=''
                                        isDisabled={false}
                                        showDollar={false}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <div className='o-fieldset__row u-mt-large'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@xs'>
                                <div className='o-fieldset__row button-group'>
                                    <button
                                        className={
                                            'c-btn c-btn--secondary u-mr-small'
                                        }
                                        type='button'
                                        onClick={onCancel}
                                    >
                                        <span className='c-btn__text'>
                                            {notify && !isError
                                                ? payments.PAYMENT_INFORMATION_RETURN_LABEL
                                                : payments.PAYMENT_INFORMATION_CANCELBTN}
                                        </span>
                                    </button>
                                    {notify && !isError ? null : (
                                        <button
                                            className={'c-btn'}
                                            type='button'
                                            onClick={onClickSubmit}
                                            disabled={
                                                !('' + payment.amount).length ||
                                                (isOperatingLease() &&
                                                    !(
                                                        (
                                                            '' +
                                                            payment.odometerReading
                                                        ).length &&
                                                        isLPAEmptyOrValid()
                                                    )) ||
                                                loading
                                            }
                                        >
                                            <span className='c-btn__text'>
                                                {loading ? (
                                                    <Spinner />
                                                ) : isAllow(
                                                      [
                                                          Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
                                                      ],
                                                      permissionsMap,
                                                      dealerID,
                                                  ) ? (
                                                    payments.PAYMENT_INFORMATION_SUBMITBTN
                                                ) : (
                                                    payments.PAYMENT_INFORMATION_SUBMITAPPBTN
                                                )}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PaymentInformation;
